import { AFRIMED_BILLING_AGREEMENT_VERSION } from "@/services/doctorBillingBrandingService.js";
import { doctorBillingAgreementState } from "./doctorBillingAgreementState.mjs";
import { loadSupportedAppointmentPaymentMethods } from "./appointmentPaymentCapabilities.js";
import supabase from "@/services/supabaseClient";
import {
  summarizeSelectedDoctorPaymentMethods,
} from "@/services/doctorPaymentPreferences.js";

const ENHANCED_PROFILE_SELECT = [
  "payout_meta",
  "signature_path",
  "practice_logo_path",
  "billing_agreement_path",
  "billing_agreement_signed_at",
  "billing_agreement_version",
  "doctor_terms_path",
].join(",");

const LEGACY_PROFILE_SELECT = [
  "payout_meta",
  "signature_path",
].join(",");

function isMissingColumnError(error) {
  const message = String(error?.message || "").toLowerCase();
  return message.includes("column") && message.includes("doctor_profiles");
}

function isUuidLike(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    String(value || "").trim(),
  );
}

async function loadDoctorProfileBillingRow(doctorUserId) {
  const enhancedResult = await supabase
    .from("doctor_profiles")
    .select(ENHANCED_PROFILE_SELECT)
    .eq("user_id", doctorUserId)
    .maybeSingle();

  if (!enhancedResult.error) return enhancedResult.data || {};
  if (!isMissingColumnError(enhancedResult.error)) throw enhancedResult.error;

  const legacyResult = await supabase
    .from("doctor_profiles")
    .select(LEGACY_PROFILE_SELECT)
    .eq("user_id", doctorUserId)
    .maybeSingle();

  if (legacyResult.error) throw legacyResult.error;
  return legacyResult.data || {};
}

export async function loadDoctorBillingReadiness(doctorUserId) {
  if (!String(doctorUserId || "").trim() || !isUuidLike(doctorUserId)) {
    return {
      servicesCount: 0,
      consultanciesCount: 0,
      paymentMethodCount: 0,
      paymentMethodLabels: [],
      signatureReady: false,
      payoutReady: false,
      practiceLogoReady: false,
      agreementReady: false,
      doctorTermsReady: false,
      agreementSignedAt: "",
      agreementVersion: "",
    };
  }

  const [profileRow, servicesResult, consultanciesResult, payoutResult, capabilities] = await Promise.all([
    loadDoctorProfileBillingRow(doctorUserId),
    supabase
      .from("doctor_services")
      .select("id", { count: "exact", head: true })
      .eq("doctor_id", doctorUserId)
      .eq("is_active", true),
    supabase
      .from("doctor_consultancies")
      .select("id", { count: "exact", head: true })
      .eq("doctor_id", doctorUserId),
    supabase.functions.invoke("clinician-payout", { body: { action: "status" } }),
    loadSupportedAppointmentPaymentMethods({ forClinicianSetup: true }).then(methods => ({ methods })).catch(() => ({ unavailable: true })),
  ]);

  if (servicesResult?.error) throw servicesResult.error;
  if (consultanciesResult?.error) throw consultanciesResult.error;

  const paymentSummary = summarizeSelectedDoctorPaymentMethods(profileRow?.payout_meta, capabilities.methods || []);
  const agreement = doctorBillingAgreementState(profileRow, AFRIMED_BILLING_AGREEMENT_VERSION);

  return {
    servicesCount: Number(servicesResult?.count || 0),
    consultanciesCount: Number(consultanciesResult?.count || 0),
    paymentMethodCount: paymentSummary.count,
    paymentMethodLabels: paymentSummary.labels,
    paymentMethods: paymentSummary.methods,
    paymentMethodsUnavailable: Boolean(capabilities.unavailable),
    signatureReady: Boolean(String(profileRow?.signature_path || "").trim()),
    payoutReady: !payoutResult.error && payoutResult.data?.profile?.verification_status === "verified",
    practiceLogoReady: Boolean(String(profileRow?.practice_logo_path || "").trim()),
    ...agreement,
    doctorTermsReady: Boolean(String(profileRow?.doctor_terms_path || "").trim()),
  };
}

export default {
  loadDoctorBillingReadiness,
};
